import {
  ChatIcon,
  CheckmarkIcon,
  DeliveryIcon,
  FilterIcon,
  LabelIcon
} from "assets/icons";
import { GoBackHeaderTitle } from "components";
import { toastServerError } from "helpers";
import { useDebouncedValue } from "hooks";
import { ContentLayout } from "layouts";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Input, Loader, Modal } from "ui";

import {
  useCampaignEstimate,
  useCreateMarketingCampaign,
  useDeleteMarketingCampaign,
  useLaunchMarketingCampaign,
  useMarketingCampaign,
  useUpdateMarketingCampaign
} from "../../apis";
import {
  ChannelCards,
  MessageComposer,
  MessageComposerValue,
  RecipientsPreviewModal,
  SectionCard,
  SegmentBuilder,
  StatusDot
} from "../../components";
import { CAMPAIGN_STATUS_COLORS, CAMPAIGN_STATUS_LABELS } from "../../constants";
import {
  CampaignEstimateParams,
  CreateMarketingCampaignPayload,
  EMarketingCampaignStatus,
  EMarketingChannel,
  EMarketingSkipReason,
  SegmentDefinition
} from "../../marketing.types";

import styles from "./MarketingCampaignBuilder.module.scss";

interface MarketingCampaignBuilderProps {
  campaignId?: string;
}

const AUTO_CHANNEL_VALUE = "auto";

const EMPTY_COMPOSER: MessageComposerValue = {
  messageText: "",
  templateName: "",
  templateParams: [],
  channel: AUTO_CHANNEL_VALUE
};

const MarketingCampaignBuilder = ({
  campaignId
}: MarketingCampaignBuilderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isNew = !campaignId;

  const { campaign, isLoading: isCampaignLoading } = useMarketingCampaign(
    campaignId ?? ""
  );

  const isDraft = isNew || campaign?.status === EMarketingCampaignStatus.Draft;

  const [name, setName] = useState("");
  const [segment, setSegment] = useState<SegmentDefinition>({});
  const [composer, setComposer] = useState<MessageComposerValue>(EMPTY_COMPOSER);
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setSegment(campaign.segmentDefinition ?? {});
      setComposer({
        messageText: campaign.messageText ?? "",
        templateName: campaign.templateName ?? "",
        templateParams: campaign.templateParams ?? [],
        channel: campaign.channel ?? AUTO_CHANNEL_VALUE
      });
    }
  }, [campaign]);

  // Cost follows the message SHAPE, not the head count: push and free-form text
  // inside a contact's 24h window are free, only an approved template is billed.
  // The backend walks the real guardrails and returns the breakdown.
  const estimateParams = useMemo<CampaignEstimateParams>(
    () => ({
      segmentDefinition: segment,
      hasText: !!composer.messageText.trim(),
      templateName: composer.templateName || undefined,
      channel:
        composer.channel === AUTO_CHANNEL_VALUE
          ? undefined
          : (composer.channel as EMarketingChannel)
    }),
    [segment, composer.messageText, composer.templateName, composer.channel]
  );

  // The estimate resolves the whole audience — same debounce the audience card
  // uses so it does not run on every keystroke.
  const debouncedParamsJson = useDebouncedValue<string>(
    JSON.stringify(estimateParams),
    500
  );
  const debouncedParams = useMemo(
    () => JSON.parse(debouncedParamsJson) as CampaignEstimateParams,
    [debouncedParamsJson]
  );

  const { estimate } = useCampaignEstimate(debouncedParams, {
    enabled: isDraft
  });

  const { mutate: createCampaign, isLoading: isCreating } =
    useCreateMarketingCampaign({
      onSuccess: (created) => {
        toast.success(t("Campaign created"));
        navigate(`/marketing/campaigns/${created.id}`, { replace: true });
      },
      onError: toastServerError
    });

  const { mutate: updateCampaign, isLoading: isUpdating } =
    useUpdateMarketingCampaign({
      onSuccess: () => toast.success(t("Campaign saved")),
      onError: toastServerError
    });

  const { mutate: launchCampaign, isLoading: isLaunching } =
    useLaunchMarketingCampaign({
      onSuccess: (result) => {
        setIsLaunchModalOpen(false);
        toast.success(
          `${t("Campaign launched")}: ${result.sent} ${t("sent")}, ${
            result.skipped
          } ${t("skipped")}`
        );
      },
      onError: toastServerError
    });

  const { mutate: deleteCampaign, isLoading: isDeleting } =
    useDeleteMarketingCampaign({
      onSuccess: () => {
        toast.success(t("Campaign deleted"));
        navigate("/marketing/campaigns", { replace: true });
      },
      onError: toastServerError
    });

  const buildPayload = (): CreateMarketingCampaignPayload | null => {
    if (!name.trim()) {
      toast.error(t("Campaign name is required"));
      return null;
    }

    if (!composer.messageText.trim() && !composer.templateName) {
      toast.error(t("Provide a message text or an approved template"));
      return null;
    }

    if (
      composer.templateName &&
      composer.templateParams.some((param) => !param.trim())
    ) {
      toast.error(t("Fill in all template variables"));
      return null;
    }

    return {
      name: name.trim(),
      segmentDefinition: segment,
      messageText: composer.messageText.trim() || undefined,
      templateName: composer.templateName || undefined,
      templateParams: composer.templateName
        ? composer.templateParams
        : undefined,
      channel:
        composer.channel === AUTO_CHANNEL_VALUE
          ? undefined
          : (composer.channel as EMarketingChannel)
    };
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (!payload) return;

    if (isNew) {
      createCampaign(payload);
    } else if (campaignId) {
      updateCampaign({ campaignId, data: payload });
    }
  };

  const count = estimate?.audience;
  const billable = estimate?.billable ?? 0;
  const estimatedCost = estimate?.estimatedCostXof.toLocaleString();
  const outsideWindow =
    estimate?.bySkipReason[EMarketingSkipReason.OutsideWindow] ?? 0;

  if (!isNew && isCampaignLoading) {
    return (
      <ContentLayout title={t("Campaign")}>
        <Loader />
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      title={
        <GoBackHeaderTitle
          title={isNew ? t("New campaign") : campaign?.name ?? t("Campaign")}
          onGoBack={() => navigate("/marketing/campaigns")}
        />
      }
      action={
        campaign && (
          <StatusDot
            color={CAMPAIGN_STATUS_COLORS[campaign.status]}
            label={CAMPAIGN_STATUS_LABELS[campaign.status]}
          />
        )
      }
    >
      <div className={styles.builder}>
        <SectionCard
          accentColor="#090914"
          icon={<LabelIcon />}
          title={t("Campaign name")}
        >
          <Input
            placeholder={t("e.g. Watches follow-up — July")}
            disabled={!isDraft}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </SectionCard>

        <SectionCard
          icon={<FilterIcon />}
          title={t("Audience · criteria")}
          subtitle={t(
            "All filters combine with AND — an unset filter does not restrict the audience."
          )}
        >
          <SegmentBuilder
            value={segment}
            onChange={setSegment}
            disabled={!isDraft}
          />

          <Button
            variant="dashed"
            size="sm"
            onClick={() => setIsPreviewOpen(true)}
          >
            {t("Preview recipients")}
          </Button>
        </SectionCard>

        <SectionCard
          accentColor="#16C350"
          icon={<ChatIcon />}
          title={t("Message")}
          subtitle={t(
            "Free text is delivered inside a contact's 24h window; outside it an approved template is required. Provide both when possible."
          )}
        >
          <MessageComposer
            value={composer}
            onChange={setComposer}
            disabled={!isDraft}
            hideChannel
          />
        </SectionCard>

        <SectionCard
          accentColor="#4A6CF7"
          icon={<DeliveryIcon />}
          title={t("Send channel")}
        >
          <ChannelCards
            value={composer.channel || AUTO_CHANNEL_VALUE}
            onChange={(channel) => setComposer({ ...composer, channel })}
            disabled={!isDraft}
          />
        </SectionCard>

        {campaign?.launchedAt && (
          <SectionCard
            accentColor="#16C350"
            icon={<CheckmarkIcon />}
            title={t("Results")}
          >
            <div className={styles.builder__results}>
              <div className={styles.builder__result}>
                <span className={styles.builder__resultValue}>
                  {campaign.sentCount}
                </span>
                <span className={styles.builder__resultLabel}>{t("Sent")}</span>
              </div>

              <div className={styles.builder__result}>
                <span className={styles.builder__resultValueMuted}>
                  {campaign.skippedCount}
                </span>
                <span className={styles.builder__resultLabel}>
                  {t("Skipped (opted out, capped, no channel)")}
                </span>
              </div>
            </div>
          </SectionCard>
        )}

        {isDraft && (
          <div className={styles.launchBar}>
            <div className={styles.launchBar__summary}>
              <span className={styles.launchBar__count}>
                ≈ {count ?? 0} {t("contacts")}
                {estimate
                  ? ` · ${estimate.deliverable} ${t("would receive")}`
                  : ""}
              </span>

              {estimate &&
                (billable ? (
                  <span className={styles.launchBar__cost}>
                    ~{estimatedCost} FCFA · {billable}{" "}
                    {t("billable template messages")}
                  </span>
                ) : (
                  <span className={styles.launchBar__free}>
                    {t("Free — nothing here is billed by WhatsApp")}
                  </span>
                ))}

              {!!outsideWindow && (
                <span className={styles.launchBar__warning}>
                  {outsideWindow}{" "}
                  {t("outside the 24h window — they need an approved template")}
                </span>
              )}
            </div>

            <div className={styles.launchBar__actions}>
              {!isNew && (
                <button
                  type="button"
                  className={styles.launchBar__delete}
                  disabled={isDeleting}
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  {t("Delete draft")}
                </button>
              )}

              <Button
                variant="default"
                loading={isCreating || isUpdating}
                onClick={handleSave}
              >
                {isNew ? t("Create draft") : t("Save draft")}
              </Button>

              {!isNew && (
                <Button onClick={() => setIsLaunchModalOpen(true)}>
                  {t("Launch campaign")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={isLaunchModalOpen}
        isLoading={isLaunching}
        title={t("Launch campaign")}
        okText={t("Launch now")}
        onClose={() => setIsLaunchModalOpen(false)}
        onOk={() => campaignId && launchCampaign(campaignId)}
      >
        <div className={styles.launchSummary}>
          <p>
            {t("The audience will be resolved now")}: ~{count ?? 0}{" "}
            {t("contacts")}.
          </p>

          {estimate && (
            <ul className={styles.launchSummary__breakdown}>
              <li>
                {estimate.deliverable} {t("would receive a message")}
              </li>
              <li>
                {billable
                  ? `${billable} ${t("billable template messages")} ≈ ${estimatedCost} FCFA`
                  : t("Free — nothing here is billed by WhatsApp")}
              </li>
              {!!outsideWindow && (
                <li>
                  {outsideWindow}{" "}
                  {t(
                    "outside the 24h window — they need an approved template"
                  )}
                </li>
              )}
            </ul>
          )}
          <p>
            {t(
              "Unsaved changes are not included — save the draft first. A launched campaign becomes immutable."
            )}
          </p>
        </div>
      </Modal>

      <Modal
        type="delete"
        isOpen={isDeleteModalOpen}
        isLoading={isDeleting}
        title={t("Delete draft")}
        okText={t("Delete")}
        onClose={() => setIsDeleteModalOpen(false)}
        onOk={() => campaignId && deleteCampaign(campaignId)}
      >
        <p className={styles.confirmText}>
          {`${t("Delete draft campaign")} "${campaign?.name ?? ""}"?`}
        </p>
      </Modal>

      <RecipientsPreviewModal
        isOpen={isPreviewOpen}
        definition={segment}
        onClose={() => setIsPreviewOpen(false)}
      />
    </ContentLayout>
  );
};

export default MarketingCampaignBuilder;
